import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { GraduationCap, ArrowRight } from 'lucide-react';

const StudentLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'student') {
        toast.success('Welcome back!');
        navigate('/student/dashboard');
      } else {
        toast.error('Please use the correct portal for your account type');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#00AEEF]/20 flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-[#00AEEF]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Student Portal</h1>
          <p className="text-[#94A3B8]">Access your courses and track your progress</p>
        </div>

        <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#94A3B8] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="student-email-input"
                placeholder="student@email.com"
                className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#94A3B8] mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="student-password-input"
                placeholder="Enter your password"
                className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="student-login-btn"
              className="w-full bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/student/reset-password" className="text-[#94A3B8] text-sm hover:text-[#00AEEF]">
              Forgot your password?
            </Link>
          </div>

          <div className="mt-6 text-center">
            <p className="text-[#94A3B8] text-sm">
              Don't have an account?{' '}
              <Link to="/enrollment" className="text-[#00AEEF] hover:text-[#00FFE0]">
                Enroll Now
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/parent/login" className="text-[#94A3B8] text-sm hover:text-white">
              Parent? Login here →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;
